import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {object} from 'yup';

import {useEffect, useState} from 'react';

import {head, innerJoin, isNil} from 'ramda';
import {isEmptyArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetInvoiceV4Query} from '@dms/api/finance';
import {FileResponseBody} from '@dms/api/shared';
import {
  useIssueVehicleWarehouseMovementMutation,
  useLazyGetVehicleWarehouseMovementQuery,
  useListAvailableVehicleWarehousesForVehicleMovementQuery,
  useListVehicleWarehousesQuery,
  useReceiveVehicleWarehouseMovementMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {getApiDateString, suffixTestId, TestIdProps, yupString} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface VehicleWarehouseStockInStockOutProps extends TestIdProps {
  vehicleId: string;
  onStockInDocumentCreation: (document: FileResponseBody) => void;
  onStockOutDocumentCreation: (document: FileResponseBody) => void;
  onMovementComplete?: () => void;
  branchId?: string;
  invoiceId: string;
}

interface VehicleWarehouseStockInStockOutFormFields {
  vehicleWarehouseId: string;
  movementAt: string;
  note: string | null;
}

export function VehicleWarehouseStockInStockOut(props: VehicleWarehouseStockInStockOutProps) {
  const isWithBranchId = isNotNilOrEmpty(props.branchId);

  const [isStockInDocumentSelected, setIsStockInDocumentSelected] = useState(true);
  const [isStockOutDocumentSelected, setIsStockOutDocumentSelected] = useState(true);
  const [stockInDocumentTemplate, setStockInDocumentTemplate] = useState<TemplateBoxTemplate>();
  const [stockOutDocumentTemplate, setStockOutDocumentTemplate] = useState<TemplateBoxTemplate>();

  const {
    data: warehouses = [],
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useListAvailableVehicleWarehousesForVehicleMovementQuery({
    vehicleId: props.vehicleId,
  });

  const {data: warehousesByBranch = []} = useListVehicleWarehousesQuery(
    {
      branchId: props.branchId,
    },
    {
      skip: isNil(props.branchId),
    }
  );
  const {data: invoice} = useGetInvoiceV4Query(
    {invoiceId: props.invoiceId},
    {skip: isNilOrEmpty(props.invoiceId)}
  );

  const availableWarehouses = isWithBranchId
    ? innerJoin(
        (warehouseByUser, warehouseByBranch) => warehouseByUser.id === warehouseByBranch.id,
        warehouses,
        warehousesByBranch
      )
    : warehouses;

  const [getTemplates, {isLoading: isTemplatesV2Loading, isError: isTemplatesV2Error}] =
    useDocumentTemplates();

  const {
    defaultTemplates: stockInDefaultTemplates,
    selectedTemplates: stockInSelectedTemplates,
    selectedAuthProfileLabel: stockInSelectedAuthProfileLabel,
    primaryTemplate: stockInPrimaryTemplate,
  } = getTemplates('receive-vehicle-warehouse-movement');
  const {
    defaultTemplates: stockOutDefaultTemplates,
    selectedTemplates: stockOutSelectedTemplates,
    selectedAuthProfileLabel: stockOutSelectedAuthProfileLabel,
    primaryTemplate: stockOutPrimaryTemplate,
  } = getTemplates('issue-vehicle-warehouse-movement');

  const [stockInVehicle] = useReceiveVehicleWarehouseMovementMutation();
  const [stockOutVehicle] = useIssueVehicleWarehouseMovementMutation();
  const [getMovement] = useLazyGetVehicleWarehouseMovementQuery();

  useEffect(() => {
    if (isNotNilOrEmpty(stockInDocumentTemplate)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStockInDocumentTemplate(stockInPrimaryTemplate);
  }, [stockInDocumentTemplate, stockInPrimaryTemplate]);

  useEffect(() => {
    if (isNotNilOrEmpty(stockOutDocumentTemplate)) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStockOutDocumentTemplate(stockOutPrimaryTemplate);
  }, [stockOutDocumentTemplate, stockOutPrimaryTemplate]);

  const warehouseOptions = availableWarehouses.map((warehouse) => ({
    label: warehouse.name,
    value: warehouse.id,
  }));

  const onSubmit: FormSubmitHandler<VehicleWarehouseStockInStockOutFormFields> = (values) =>
    stockInVehicle({
      vehicleWarehouseId: values.vehicleWarehouseId,
      body: {
        vehicleId: props.vehicleId,
        movementAt: values.movementAt,
        receiptTemplateId: isStockInDocumentSelected ? stockInDocumentTemplate?.id : undefined,
        note: values.note,
      },
    })
      .unwrap()
      .then((response) => {
        if (!isStockInDocumentSelected) {
          showNotification.success(i18n.t('entity.vehicle.labels.vehicleStockedIn'));
          closeCurrentDialog();

          return;
        }

        return response;
      })
      .then((response) => {
        if (!response) {
          return;
        }

        return getMovement({
          vehicleWarehouseMovementId: response?.id,
        })
          .unwrap()
          .then((response) => {
            response.document && props.onStockInDocumentCreation(response.document);
            stockOutVehicle({
              vehicleWarehouseId: response.vehicleWarehouseId,
              body: {
                vehicleId: props.vehicleId,
                movementAt: invoice?.issueDate ?? values.movementAt,
                issueTemplateId: isStockOutDocumentSelected
                  ? stockOutDocumentTemplate?.id
                  : undefined,
                note: values.note,
              },
            })
              .unwrap()
              .then((response) => {
                if (!isStockOutDocumentSelected) {
                  showNotification.success(i18n.t('entity.vehicle.labels.vehicleStockedOut'));
                  closeCurrentDialog();

                  return;
                }

                return response;
              })
              .then((response) => {
                if (!response) {
                  return;
                }

                return getMovement({
                  vehicleWarehouseMovementId: response?.id,
                })
                  .unwrap()
                  .then((response) => {
                    response.document && props.onStockOutDocumentCreation(response.document);
                  });
              })
              .catch(handleApiError);
          });
      })
      .then(props.onMovementComplete)
      .catch(handleApiError);

  const defaultValues: Partial<VehicleWarehouseStockInStockOutFormFields> = {
    vehicleWarehouseId: head(warehouseOptions)?.value,
    movementAt: getApiDateString(new Date()),
  };

  const isLoading = isWarehousesLoading || isTemplatesV2Loading;
  const isError = isWarehousesError || isTemplatesV2Error;

  return (
    <VStack spacing={4}>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isWithBranchId && isEmptyArray(availableWarehouses)}
        emptyMessage={i18n.t('entity.vehicleWarehouse.labels.noWarehouseForBranch')}
      >
        <Form<VehicleWarehouseStockInStockOutFormFields>
          defaultValues={defaultValues}
          onSubmit={onSubmit}
          schema={schema}
        >
          {(control, formApi) => (
            <VStack spacing={4}>
              <Grid columns={2}>
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.warehouse')}
                  name="vehicleWarehouseId"
                  type="choice"
                  helperText={
                    isWithBranchId
                      ? i18n.t('entity.vehicleWarehouse.labels.showingWarehousesForBranch')
                      : undefined
                  }
                  options={warehouseOptions}
                  control={control}
                  isNotClearable
                  isRequired
                  data-testid={suffixTestId('vehicleWarehouseId-select', props)}
                />
                <FormField
                  label={i18n.t('entity.vehicleWarehouse.labels.dateOfStockIn')}
                  name="movementAt"
                  type="apiDate"
                  control={control}
                  isRequired
                  data-testid={suffixTestId('movementAt', props)}
                />
              </Grid>
              <DocumentTemplateBox
                data-testid={suffixTestId('stockIn-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockInDocument')}
                templates={stockInDefaultTemplates}
                authorizationTemplates={stockInSelectedTemplates}
                authorizationProfileLabel={stockInSelectedAuthProfileLabel}
                selectedTemplate={stockInDocumentTemplate}
                isSelected={isStockInDocumentSelected}
                onSelect={setIsStockInDocumentSelected}
                onTemplateChange={setStockInDocumentTemplate}
                isDisabled
              />
              <DocumentTemplateBox
                data-testid={suffixTestId('stockOut-documentTemplateBox', props)}
                title={i18n.t('entity.document.labels.vehicleWarehouseStockOutDocument')}
                templates={stockOutDefaultTemplates}
                authorizationTemplates={stockOutSelectedTemplates}
                authorizationProfileLabel={stockOutSelectedAuthProfileLabel}
                selectedTemplate={stockOutDocumentTemplate}
                isSelected={isStockOutDocumentSelected}
                onSelect={setIsStockOutDocumentSelected}
                onTemplateChange={setStockOutDocumentTemplate}
                onNoteChange={(value) => formApi.setValue('note', value)}
                noteLabel={i18n.t('entity.document.labels.vehicleWarehouseTransferNote')}
                isWithNote
                isDisabled
              />
              <ButtonGroup align="right">
                <Button
                  title={i18n.t('general.actions.cancel')}
                  variant="secondary"
                  onClick={closeCurrentDialog}
                  data-testid={suffixTestId('cancel-button', props)}
                />
                <FormButton
                  title={i18n.t('general.actions.confirm')}
                  type="submit"
                  control={control}
                  data-testid={suffixTestId('submit-button', props)}
                />
              </ButtonGroup>
            </VStack>
          )}
        </Form>
      </DataStatus>
    </VStack>
  );
}

const schema = object({
  vehicleWarehouseId: yupString.required(),
  movementAt: yupString.required(),
  note: yupString,
});
